/**
 * One airline or car company — `/hotel-supplier/airlines/:uid` and
 * `/hotel-supplier/car-rental/:uid`.
 *
 * The header is the synced vendor record (code, alternate code, website), which
 * nobody edits here: it is Andavo's vendor table, mirrored daily, and an edit
 * would be overwritten by the next sync. Below it are the two things Vendor
 * Relations OWNS and edits — the people (VendorContacts) and the contracts
 * (VendorContracts) — each fetching and refetching on its own.
 *
 * A uid that belongs to the OTHER tab is shown as not found rather than rendered
 * under the wrong heading. Codes collide across types, so "Hertz" on the Airlines
 * tab would be the exact confusion the per-type tabs exist to prevent.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { Field, Group } from '@/features/crm/components/DetailPanel';
import { fmtMaybeInstant } from '@/features/crm/lib/dates';
import { VendorContacts } from '../../components/VendorContacts';
import {
  VendorContracts,
  type VendorContract,
} from '../../components/VendorContracts';
import type { SupplierContactRow } from '../../components/SupplierContacts';
import { VendorStatusBadge } from './VendorsPage';
import { VENDOR_KINDS, type Vendor, type VendorType } from './vendor-kinds';

export default function VendorDetailPage({ type }: { type: VendorType }) {
  const kind = VENDOR_KINDS[type];
  const t = `${kind.testId}-detail`;
  const { uid = '' } = useParams<{ uid: string }>();

  const [vendor, setVendor] = useState<Vendor | null>(null);
  /**
   * The two owned sections' first loads, fetched IN PARALLEL with the vendor and
   * handed down, so the whole page renders in one commit. `undefined` when that
   * request failed: the section then fetches for itself and shows its own error
   * state, which is more useful than failing the whole page over one section.
   */
  const [firstContacts, setFirstContacts] = useState<SupplierContactRow[]>();
  const [firstContracts, setFirstContracts] = useState<VendorContract[]>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const enc = encodeURIComponent(uid);
    const contactsReq = apiFetch<{ contacts: SupplierContactRow[] }>(
      `/api/btp/vendor-contacts?vendor_uid=${enc}`,
    );
    const contractsReq = apiFetch<{
      contracts: VendorContract[];
      truncated?: boolean;
    }>(`/api/btp/vendor-contracts?vendor_uid=${enc}`);
    // Settled, never awaited bare: a rejection here must not become an
    // unhandled one while the vendor request decides the page.
    const sections = Promise.allSettled([contactsReq, contractsReq]);
    apiFetch<{ vendor: Vendor }>(`/api/btp/vendors/${enc}`)
      .then(async (d) => {
        const [c, k] = await sections;
        if (cancelled) return;
        if (d.vendor.service_type !== type) {
          setVendor(null);
          setError(`No ${kind.noun} with this id.`);
          return;
        }
        setFirstContacts(
          c.status === 'fulfilled' ? c.value.contacts : undefined,
        );
        // A capped first page is left to the section's own fetch, which is
        // the one that reports the cap.
        setFirstContracts(
          k.status === 'fulfilled' && !k.value.truncated
            ? k.value.contracts
            : undefined,
        );
        setVendor(d.vendor);
      })
      .catch((e) => {
        if (!cancelled) {
          setVendor(null);
          setError(e instanceof ApiError ? e.message : String(e));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid, type, kind.noun]);

  const back = (
    <Link className="row-link" style={{ fontSize: 12.5 }} to={kind.path}>
      ← {kind.title}
    </Link>
  );

  if (loading) {
    return (
      <div className="page" data-testid={t}>
        <div className="empty-state">
          <div className="empty-sub">Loading…</div>
        </div>
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="page" data-testid={t}>
        <div className="page-header">{back}</div>
        <div
          className="banner banner-error"
          data-testid={`${t}-error`}
          role="alert"
        >
          {error ?? `No ${kind.noun} with this id.`}
        </div>
      </div>
    );
  }

  return (
    <div className="page" data-testid={t}>
      <div className="page-header">
        {back}
        <h1 data-testid={`${t}-name`} style={{ marginTop: 6 }}>
          {vendor.name}
        </h1>
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            flexWrap: 'wrap',
            marginTop: 6,
          }}
        >
          <span className="mono" style={{ fontSize: 12.5 }}>
            {vendor.code}
            {vendor.code_alternate ? ` / ${vendor.code_alternate}` : ''}
          </span>
          <VendorStatusBadge vendor={vendor} />
        </div>
      </div>

      <div className="section">
        <div className="section-header">
          <span className="section-label">Vendor</span>
          <span className="section-meta">
            From Andavo&rsquo;s vendor list · synced{' '}
            {fmtMaybeInstant(vendor.synced_at) ?? '—'}
          </span>
        </div>
        <div className="section-body">
          <Group label="Record" testId={`${t}-record`}>
            <dl className="detail-grid">
              <Field label="Name" value={vendor.name} />
              <Field label={kind.codeLabel} mono value={vendor.code} />
              <Field
                label={kind.altCodeLabel}
                mono
                value={vendor.code_alternate}
              />
              <Field label="Website">
                {/* The server keeps only http(s) URLs, so this is never a
                    javascript: link; noopener because it is someone else's site. */}
                {vendor.website_url ? (
                  <a
                    data-testid={`${t}-website`}
                    href={vendor.website_url}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {vendor.website_url}
                  </a>
                ) : null}
              </Field>
              <Field label="Status">
                <VendorStatusBadge vendor={vendor} />
              </Field>
            </dl>
          </Group>
          {vendor.removed_from_source_at && (
            <p className="detail-note" data-testid={`${t}-removed-note`}>
              <span className="muted">
                This vendor is no longer in Andavo&rsquo;s vendor list (since{' '}
                {fmtMaybeInstant(vendor.removed_from_source_at) ?? '—'}). Its
                contacts and contracts are kept here.
              </span>
            </p>
          )}
        </div>
      </div>

      <div className="section">
        <div className="section-header">
          <span className="section-label">Contacts</span>
        </div>
        <div className="section-body">
          <VendorContacts
            initialContacts={firstContacts}
            testId={`${t}-contacts`}
            vendorUid={vendor.uid}
          />
        </div>
      </div>

      <div className="section">
        <div className="section-header">
          <span className="section-label">Contracts</span>
        </div>
        <div className="section-body">
          <VendorContracts
            initialContracts={firstContracts}
            testId={`${t}-contracts`}
            vendorUid={vendor.uid}
          />
        </div>
      </div>
    </div>
  );
}
